/**
 * Record detail builders shared by the dashboards: each returns a RecordDetail whose rows
 * carry the field names a query can be raised against.
 */
import type {
  AdverseEvent,
  Deviation,
  FormRecord,
  Lab,
  Query,
  Subject,
  Visit,
  Vitals,
  Dosing,
  ConMed,
} from '@clinspace/shared';
import type { RecordDetail } from './Dashboard.ts';
import type { StudyView } from './StudyView.ts';

const dash = (v: string | number | undefined | null) =>
  v === undefined || v === null || v === '' ? '—' : String(v);

export function subjectDetail(view: StudyView, s: Subject): RecordDetail {
  return {
    ref: view.ref(s.id, 'Demographics', `F-${s.id}-SCR-demographics`, 'Screening'),
    title: `Subject ${s.id}`,
    currentValue: s.status,
    rows: [
      { label: 'Site', value: view.siteLabel(s.siteId) },
      { label: 'Status', value: s.status, field: 'Status' },
      { label: 'Status date', value: s.statusDate, field: 'Status date' },
      { label: 'Arm', value: dash(s.arm), field: 'Arm' },
      { label: 'Sex / age', value: `${s.sex} / ${s.age}`, field: 'Sex' },
      {
        label: 'Consent date',
        value: s.consentDate,
        field: 'Consent date',
        flag: s.screeningDate < s.consentDate,
      },
      {
        label: 'Screening date',
        value: s.screeningDate,
        field: 'Screening date',
        flag: s.screeningDate < s.consentDate,
      },
      { label: 'Randomisation', value: dash(s.randomisationDate), field: 'Randomisation date' },
      { label: 'First dose', value: dash(s.firstDoseDate), field: 'First dose date' },
      { label: 'Height / weight', value: `${s.heightCm} cm / ${s.weightKg} kg`, field: 'Weight' },
      { label: 'BMI', value: `${s.bmi} (${s.bmiBucket})`, field: 'BMI' },
      { label: 'Screen fail reason', value: dash(s.screenFailReason), field: 'Screening outcome' },
      {
        label: 'Discontinuation',
        value: s.discontinuationReason
          ? `${s.discontinuationReason} (${s.discontinuationDate})`
          : '—',
        field: 'Discontinuation reason',
      },
      { label: 'Flags', value: s.flags.length ? s.flags.join(', ') : 'none' },
      { label: 'Open queries', value: String(view.openQueryCount(s.id)) },
    ],
  };
}

export function visitDetail(view: StudyView, v: Visit): RecordDetail {
  return {
    ref: view.ref(v.subjectId, 'Visit', v.id, v.visitName),
    title: `${v.subjectId} · ${v.visitName}`,
    currentValue: v.actualDate ?? v.status,
    rows: [
      { label: 'Subject', value: v.subjectId },
      { label: 'Site', value: view.siteLabel(v.siteId) },
      { label: 'Status', value: v.status, field: 'Visit status' },
      {
        label: 'Timing',
        value: v.timing,
        flag: v.timing === 'Late' || v.timing === 'Early' || v.timing === 'Missed',
      },
      { label: 'Target date', value: v.targetDate },
      { label: 'Window', value: `${v.windowStart} to ${v.windowEnd}` },
      {
        label: 'Actual date',
        value: dash(v.actualDate),
        field: 'Visit date',
        flag: v.timing === 'Late' || v.timing === 'Early',
      },
      { label: 'Deviation (days)', value: dash(v.deviationDays) },
      {
        label: 'Missed reason',
        value: dash(v.missedReason),
        field: 'Missed visit reason',
        flag: v.status === 'Missed',
      },
    ],
  };
}

export function formDetail(view: StudyView, f: FormRecord): RecordDetail {
  const rows: RecordDetail['rows'] = [
    { label: 'Subject', value: f.subjectId },
    { label: 'Visit', value: f.visitCode },
    { label: 'Form', value: f.form },
    {
      label: 'Status',
      value: f.status,
      field: 'Page status',
      flag: f.status === 'Not Started' && f.overdue,
    },
    { label: 'Due', value: `${f.dueDate}${f.overdue ? ' (overdue)' : ''}` },
    { label: 'Entered', value: dash(f.enteredAt) },
    { label: 'Verified', value: dash(f.verifiedAt) },
    { label: 'Signed / locked', value: `${dash(f.signedAt)} / ${dash(f.lockedAt)}` },
    {
      label: 'Fields',
      value: `${f.totalFields - f.missingFields.length} of ${f.totalFields} present`,
    },
  ];
  for (const m of f.missingFields) rows.push({ label: 'Missing', value: m, field: m, flag: true });
  return {
    ref: view.ref(f.subjectId, f.form, f.id, view.visitName(f.visitId)),
    title: `${f.form} · ${f.subjectId} · ${f.visitCode}`,
    currentValue: f.status,
    rows,
  };
}

export function labDetail(view: StudyView, l: Lab): RecordDetail {
  return {
    ref: view.ref(l.subjectId, 'Labs', l.id, view.visitName(l.visitId), l.analyte),
    title: `${l.analyte} · ${l.subjectId} · ${l.visitCode}`,
    currentValue: `${l.value} ${l.unit}`,
    rows: [
      { label: 'Subject', value: l.subjectId },
      { label: 'Site', value: view.siteLabel(l.siteId) },
      { label: 'Date', value: l.date, field: 'Collection date' },
      { label: 'Panel', value: l.panel },
      { label: 'Value', value: `${l.value} ${l.unit}`, field: l.analyte, flag: l.grade >= 3 },
      { label: 'Reference range', value: `${l.low} – ${l.high} ${l.unit}` },
      { label: 'Flag', value: dash(l.abnormalFlag) },
      { label: 'CTCAE grade', value: String(l.grade), field: 'Grade', flag: l.grade >= 3 },
      { label: 'ULN multiple', value: l.ulnMultiple !== undefined ? `${l.ulnMultiple}×` : '—' },
      {
        label: 'Clinically significant',
        value: dash(l.clinicallySignificant),
        field: 'Clinical significance',
        flag: l.clinicallySignificant === 'Pending',
      },
    ],
  };
}

export function aeDetail(view: StudyView, a: AdverseEvent): RecordDetail {
  const s = view.subject(a.subjectId);
  const beforeDose = !!s?.firstDoseDate && a.onsetDate < s.firstDoseDate;
  const rows: RecordDetail['rows'] = [
    { label: 'Subject', value: a.subjectId },
    { label: 'Site', value: view.siteLabel(a.siteId) },
    { label: 'Term', value: a.term, field: 'AE term' },
    { label: 'SOC', value: a.soc },
    {
      label: 'Onset',
      value: `${a.onsetDate}${a.onsetStudyDay !== undefined ? ` (day ${a.onsetStudyDay})` : ''}`,
      field: 'AE Onset Date',
      flag: beforeDose,
    },
    {
      label: 'Resolution',
      value: a.ongoing ? 'ongoing' : dash(a.resolutionDate),
      field: 'AE Resolution Date',
      flag: !!a.resolutionDate && a.resolutionDate < a.onsetDate,
    },
    { label: 'Grade', value: String(a.grade), field: 'Severity grade', flag: a.grade >= 3 },
    {
      label: 'Serious',
      value: a.serious ? `Yes: ${a.seriousnessCriteria.join(', ')}` : 'No',
      field: 'Serious',
    },
    { label: 'Relatedness', value: a.relatedness, field: 'Relatedness' },
    {
      label: 'Expected',
      value: a.expected ? 'Yes' : 'No (unexpected)',
      field: 'Expectedness',
      flag: !a.expected && a.serious,
    },
    { label: 'Action taken', value: a.actionTaken, field: 'Action taken' },
    { label: 'Outcome', value: a.outcome, field: 'Outcome', flag: a.outcome === 'Fatal' },
    { label: 'Reported', value: a.reportedDate, field: 'Reported date' },
  ];
  if (a.narrative) rows.push({ label: 'Narrative', value: a.narrative, field: 'Narrative' });
  return {
    ref: view.ref(a.subjectId, 'Adverse Events', a.id, undefined),
    title: `${a.serious ? 'SAE' : 'AE'} · ${a.term} · ${a.subjectId}`,
    currentValue: a.onsetDate,
    rows,
  };
}

export function deviationDetail(view: StudyView, d: Deviation): RecordDetail {
  return {
    ref: view.ref(d.subjectId, 'Protocol Deviation', d.id, view.visitName(d.visitId)),
    title: `Deviation ${d.id} · ${d.subjectId}`,
    currentValue: d.classification ?? 'Unclassified',
    rows: [
      { label: 'Subject', value: d.subjectId },
      { label: 'Site', value: view.siteLabel(d.siteId) },
      { label: 'Category', value: d.category, field: 'Category' },
      {
        label: 'Classification',
        value: d.classification ?? 'Unclassified',
        field: 'Classification',
        flag: d.classification === null,
      },
      { label: 'Date', value: d.date, field: 'Deviation date' },
      { label: 'Reported', value: d.reportedDate },
      { label: 'Visit', value: dash(d.visitCode) },
      { label: 'Description', value: d.description, field: 'Description' },
      {
        label: 'Corrective action',
        value: dash(d.correctiveAction),
        field: 'Corrective action',
        flag: !d.correctiveAction,
      },
      { label: 'Status', value: d.status, field: 'Status' },
    ],
  };
}

export function vitalsDetail(view: StudyView, v: Vitals, previous?: Vitals): RecordDetail {
  const delta = previous ? v.weightKg - previous.weightKg : 0;
  return {
    ref: view.ref(v.subjectId, 'Vitals', v.id, view.visitName(v.visitId)),
    title: `Vitals · ${v.subjectId} · ${v.visitCode}`,
    currentValue: `${v.weightKg} kg`,
    rows: [
      { label: 'Date', value: v.date, field: 'Assessment date' },
      {
        label: 'Blood pressure',
        value: `${v.systolicBp} / ${v.diastolicBp} mmHg`,
        field: 'Systolic BP',
      },
      { label: 'Heart rate', value: `${v.heartRate} bpm`, field: 'Heart rate' },
      { label: 'Temperature', value: `${v.temperatureC} °C`, field: 'Temperature' },
      { label: 'Respiratory rate', value: `${v.respiratoryRate} /min`, field: 'Respiratory rate' },
      {
        label: 'Weight',
        value: `${v.weightKg} kg${previous ? ` (${delta > 0 ? '+' : ''}${delta.toFixed(1)} kg vs previous)` : ''}`,
        field: 'Weight',
        flag: Math.abs(delta) >= 10,
      },
    ],
  };
}

export function dosingDetail(view: StudyView, d: Dosing): RecordDetail {
  return {
    ref: view.ref(d.subjectId, 'Dosing', d.id, view.visitName(d.visitId)),
    title: `Dosing · ${d.subjectId} · ${d.visitCode}`,
    currentValue: d.administered ? `${d.dose} ${d.unit}` : 'Not administered',
    rows: [
      { label: 'Date', value: d.date, field: 'Dose date' },
      { label: 'Drug', value: d.drug },
      {
        label: 'Administered',
        value: d.administered ? 'Yes' : 'No',
        field: 'Dose administered',
        flag: !d.administered,
      },
      { label: 'Dose', value: `${d.dose} ${d.unit}`, field: 'Dose amount' },
      {
        label: 'Doses missed',
        value: String(d.missedDoses),
        field: 'Doses missed',
        flag: d.missedDoses > 2,
      },
      { label: 'Reason', value: dash(d.reason), field: 'Reason missed' },
    ],
  };
}

export function conMedDetail(view: StudyView, c: ConMed): RecordDetail {
  return {
    ref: view.ref(c.subjectId, 'Concomitant Medications', c.id),
    title: `Con-med · ${c.drug} · ${c.subjectId}`,
    currentValue: `${c.dose} ${c.unit} ${c.frequency}`,
    rows: [
      { label: 'Medication', value: c.drug, field: 'Medication' },
      { label: 'Indication', value: c.indication, field: 'Indication' },
      { label: 'Dose', value: `${c.dose} ${c.unit} ${c.route} ${c.frequency}`, field: 'Dose' },
      { label: 'Start', value: c.startDate, field: 'Start date' },
      { label: 'End', value: c.ongoing ? 'ongoing' : dash(c.endDate), field: 'End date' },
    ],
  };
}

export function queryDetail(view: StudyView, q: Query): RecordDetail {
  const rows: RecordDetail['rows'] = [
    { label: 'Subject', value: q.subjectId },
    { label: 'Site', value: view.siteLabel(q.siteId) },
    {
      label: 'Record',
      value: `${q.form}${q.field ? ` · ${q.field}` : ''}${q.visit ? ` · ${q.visit}` : ''}`,
    },
    { label: 'Value', value: dash(q.currentValue) },
    { label: 'Category / priority', value: `${q.category} / ${q.priority}` },
    { label: 'Status', value: q.status, flag: q.status === 'Open' },
    { label: 'Raised', value: `${q.raisedAt.slice(0, 10)} by ${q.raisedBy.name}` },
    { label: 'Text', value: q.text },
  ];
  for (const r of q.responses)
    rows.push({ label: `Reply ${r.at.slice(0, 10)}`, value: `${r.by}: ${r.text}` });
  return {
    ref: {
      study: q.study,
      subjectId: q.subjectId,
      form: q.form,
      recordId: q.recordId,
      ...(q.visit ? { visit: q.visit } : {}),
      ...(q.field ? { field: q.field } : {}),
    },
    title: `Query ${q.id.slice(-6)} · ${q.subjectId}`,
    currentValue: q.currentValue,
    rows,
  };
}
